// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type { AppRouteKey } from "../types/routing";

export const APP_LOCALES = ["en", "es"] as const;
export type AppLocale = (typeof APP_LOCALES)[number];

export const LOCALE_PREFERENCE_KEY = "locale";

const INTL_TAG: Record<AppLocale, string> = {
  en: "en-US",
  es: "es-US",
};

export function parseAppLocale(value: string | null | undefined): AppLocale {
  return value === "es" ? "es" : "en";
}

export function intlTag(locale: AppLocale): string {
  return INTL_TAG[locale];
}

const SENTINEL_YEAR_CEILING = 100;

function toValidDate(value: string | null | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) || parsed.getUTCFullYear() < SENTINEL_YEAR_CEILING
    ? undefined
    : parsed;
}

export function formatDateOnlyInLocale(
  value: string | null | undefined,
  locale: AppLocale,
): string {
  const parsed = toValidDate(value);
  if (!parsed) {
    return "";
  }
  return new Intl.DateTimeFormat(intlTag(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

export function formatDateTimeInLocale(
  value: string | null | undefined,
  locale: AppLocale,
  timeZone?: string,
): string {
  const parsed = toValidDate(value);
  if (!parsed) {
    return "";
  }
  return new Intl.DateTimeFormat(intlTag(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).format(parsed);
}

export function formatWholeCurrencyInLocale(
  value: number,
  locale: AppLocale,
): string {
  return new Intl.NumberFormat(intlTag(locale), {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

export interface NavLabel {
  short: string;
  desc: string;
}

export interface ShellCopy {
  nav: Partial<Record<AppRouteKey, NavLabel>>;
  expandNavigation: string;
  collapseNavigation: string;
  closeNavigation: string;
  primaryNavigation: string;
  languageTitle: string;
  languageHint: string;
  english: string;
  spanish: string;
}

const EN_NAV: ShellCopy["nav"] = {
  contacts: { short: "Contacts", desc: "CRM and Relationships" },
  journeys: { short: "Journeys", desc: "Client and Property Progression" },
  prospecting: { short: "Prospecting", desc: "Leads, Outreach and Conversion" },
  communications: { short: "Communications", desc: "Channels and Threads" },
  property: { short: "Property", desc: "Portfolio and Operations" },
  vendors: { short: "Vendors", desc: "Qualification and Credentials" },
  workOrders: { short: "Work Orders", desc: "Maintenance and Dispatch" },
  workloadForecasts: { short: "Forecasts", desc: "Workload Collision Forecasts" },
  delegations: { short: "Delegations", desc: "Assistant and Vendor Delegation" },
  relationships: { short: "Relationships", desc: "Client Care and Relationships" },
  insights: { short: "Daily Brief", desc: "Ranked Priorities and Tasks" },
  documents: { short: "Documents", desc: "Templates and Generated PDFs" },
};

const ES_NAV: ShellCopy["nav"] = {
  contacts: { short: "Contactos", desc: "CRM y relaciones" },
  journeys: { short: "Recorridos", desc: "Avance del cliente y la propiedad" },
  prospecting: { short: "Prospección", desc: "Prospectos, contacto y conversión" },
  communications: { short: "Comunicaciones", desc: "Canales y conversaciones" },
  property: { short: "Propiedad", desc: "Cartera y operaciones" },
  vendors: { short: "Proveedores", desc: "Calificación y credenciales" },
  workOrders: { short: "Órdenes de trabajo", desc: "Mantenimiento y despacho" },
  workloadForecasts: { short: "Pronósticos", desc: "Pronósticos de carga de trabajo" },
  delegations: { short: "Delegaciones", desc: "Delegación a asistentes y proveedores" },
  relationships: { short: "Relaciones", desc: "Cuidado del cliente" },
  insights: { short: "Resumen del día", desc: "Prioridades y tareas" },
  documents: { short: "Documentos", desc: "Plantillas y PDF generados" },
};

const EN_SHELL: ShellCopy = {
  nav: EN_NAV,
  expandNavigation: "Expand navigation",
  collapseNavigation: "Collapse navigation",
  closeNavigation: "Close navigation",
  primaryNavigation: "Primary application navigation",
  languageTitle: "Language",
  languageHint:
    "Navigation, dates, and currency follow this language. Saved to your account.",
  english: "English",
  spanish: "Español",
};

const ES_SHELL: ShellCopy = {
  nav: ES_NAV,
  expandNavigation: "Ampliar navegación",
  collapseNavigation: "Contraer navegación",
  closeNavigation: "Cerrar navegación",
  primaryNavigation: "Navegación principal de la aplicación",
  languageTitle: "Idioma",
  languageHint:
    "La navegación, las fechas y la moneda siguen este idioma. Se guarda en tu cuenta.",
  english: "English",
  spanish: "Español",
};

export function shellCopy(locale: AppLocale): ShellCopy {
  return locale === "es" ? ES_SHELL : EN_SHELL;
}

export interface LocaleSyncState {
  hydrated: boolean;
  dirty: boolean;
  lastSynced: AppLocale | null;
}

export interface LocaleSyncDecision {
  state: LocaleSyncState;
  apply: AppLocale | null;
  write: AppLocale | null;
}

export function onLocaleHydrated(
  state: LocaleSyncState,
  local: AppLocale,
  remote: AppLocale | null,
): LocaleSyncDecision {
  if (state.dirty) {
    return {
      state: { hydrated: true, dirty: false, lastSynced: local },
      apply: null,
      write: local,
    };
  }
  if (remote !== null && remote !== local) {
    return {
      state: { hydrated: true, dirty: false, lastSynced: remote },
      apply: remote,
      write: null,
    };
  }
  return {
    state: { hydrated: true, dirty: false, lastSynced: remote ?? local },
    apply: null,
    write: null,
  };
}

export function onLocaleChanged(
  state: LocaleSyncState,
  locale: AppLocale,
): LocaleSyncDecision {
  if (!state.hydrated) {
    return {
      state: { ...state, dirty: true },
      apply: null,
      write: null,
    };
  }
  if (state.lastSynced === locale) {
    return { state, apply: null, write: null };
  }
  return {
    state: { hydrated: true, dirty: false, lastSynced: locale },
    apply: null,
    write: locale,
  };
}

export function onLocaleWriteFailed(state: LocaleSyncState): LocaleSyncState {
  return { ...state, lastSynced: null };
}
